import React, { useEffect } from 'react'
import * as ReactErrorBoundary from 'react-error-boundary'
import * as Sentry from "@sentry/react";
import { PostgrestError } from '@supabase/supabase-js';
import { useRouteError } from 'react-router';

/**
 * A 429 from the backend is a retryable limit, not an application crash.
 * HealthShareApiError carries `status`. Laravel's throttle page, and the
 * client's non-JSON fallback, only carry it in the message.
 */
export function isRateLimitedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  if ('status' in error && (error as { status?: unknown }).status === 429) {
    return true
  }
  const message = error instanceof Error ? error.message : ''
  return /too many attempts|request failed:\s*429/i.test(message)
}

const InnerErrorBoundary = ReactErrorBoundary.ErrorBoundary

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ErrorBoundaryProps {

}

/**
 * Normalizes whatever was thrown into an Error and reports it to Sentry.
 *
 * Shared by the React error boundary below and by RouteErrorFallback, so that an
 * error caught by react-router is reported exactly the same way as one that
 * reaches the boundary around <App />.
 */
function captureAppError(error: unknown): void {
  console.error('ErrorBoundary', error)
  let err: Error;
  if ((error instanceof Error)) {
    err = error;
  } else {
    if (isPostgrestError(error)) {
      err = new Error(error.message, {
        cause: error,
      });
    } else {
      err = new Error(JSON.stringify(error), {
        cause: error,
      });
    }
  }

  const eventId = Sentry.captureException(err)
  Sentry.showReportDialog({ eventId })
}

export function ErrorBoundary({children}: React.PropsWithChildren<ErrorBoundaryProps>) {
  return <InnerErrorBoundary
      fallbackRender={({ error }) => <ErrorFallback error={error} />}
      onError={(error: unknown) => {
        if (!isRateLimitedError(error)) {
          captureAppError(error)
        }
      }} >
    {children}
  </InnerErrorBoundary>
}

/**
 * The `errorElement` for the router's root route -- use this rather than a bare
 * <ErrorFallback />.
 *
 * react-router catches a render error thrown anywhere beneath a route and renders
 * that route's `errorElement` instead of rethrowing, so the <ErrorBoundary> wrapped
 * around <App /> in index.tsx never sees it. Every screen in this app is a child of
 * the root route, so pointing `errorElement` straight at <ErrorFallback /> made every
 * route render error invisible to Sentry -- while the fallback told the user "we've
 * already been notified of the problem". This component closes that gap: it reports
 * first, then renders the same fallback.
 */
export function RouteErrorFallback() {
  const error = useRouteError()

  // Effect rather than inline so one error is reported once, not on every re-render.
  useEffect(() => {
    if (!isRateLimitedError(error)) {
      captureAppError(error)
    }
  }, [error])

  return <ErrorFallback error={error} />
}

export function ErrorFallback({ error }: { error?: unknown }) {
  const rateLimited = isRateLimitedError(error)

  return <div className="container">
    <div className='row'>
      <div className='col col-lg-6 offset-lg-3'>
        <div className="error-fallback-screen">
          <h1>{rateLimited ? 'Please wait a moment' : 'Something went wrong!'}</h1>

          {rateLimited ? (
            <p>
              Too many requests were sent in a short time. Wait a minute, then reload your browser.
            </p>
          ) : (
          <p>
            We're sorry, something went wrong.  We've already been notified of the problem.  If you continue to see
            errors, please reach out at <a href="mailto:gordon@healthsharetech.com">gordon@healthsharetech.com</a>
          </p>
          )}

          <button className="btn btn-primary"
              onClick={() => window.location.href = '/'}>
            Reload your browser
          </button>
        </div>
      </div>
    </div>
  </div>
}


function isPostgrestError(error: any): error is PostgrestError {
  return error && typeof error === 'object' && 'message' in error && 'code' in error;
}
